import { mockEventsList } from '../../__mocks__/events/eventsList';
import { createHttpService } from '../ApiService';
import { normalizePaginatedResponse } from '../__shared/paginationUtils';
import { TPaginationParams } from '../__shared/shared.interfaces';
import {
  TEventsApiResponse,
  TEventsPrimary,
  TEventsMonthWindowResponse,
  TMonthWindowRawEvent,
  TEventsMonthWindowFlattened,
} from './events.interfaces';
import dayjs from 'dayjs';

export const useEventsService = () => {
  const httpAPI = createHttpService();
  const baseEventPath = '';

  // Sub-endpoints for specific event filters
  // const eventsByManagerIdEndpoint = "/by-managerId";
  // const eventsByPlannerIdEndpoint = "/by-plannerId";
  // const eventsByInstituteIdEndpoint = "/by-instituteId";
  // const eventByIdEndpoint = "/by-id";

  const getEventsList = (
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    _params: TPaginationParams
  ): Promise<TEventsApiResponse> => {
    const requestObj = {
      params: {
        page: Number(_params.page) || 1,
        limit: Number(_params.limit) || 10,
      },
    };

    return httpAPI.events
      .get<any>(`${baseEventPath}`, requestObj)
      .then((res) => {
        const body = res?.data ?? res;
        if (body && (Array.isArray(body.data) || body.data === undefined)) {
          return normalizePaginatedResponse<TEventsPrimary>(body, 'data');
        }
        return normalizePaginatedResponse<TEventsPrimary>({}, 'data');
      })
      .catch(() =>
        normalizePaginatedResponse<TEventsPrimary>(
          {
            data: mockEventsList.slice(
              (Number(_params.page) - 1) * Number(_params.limit),
              Number(_params.page) * Number(_params.limit)
            ),
            total: mockEventsList.length,
            page: Number(_params.page) || 1,
            limit: Number(_params.limit) || 10,
          },
          'data'
        )
      );
  };

  //  // Create a new event
  // const createEvent = (eventData: TInstitutionEvent): Promise<TInstitutionEvent> => {
  //   return httpAPI.post<TInstitutionEvent>(`${baseEventPath}`, eventData);
  // };

  // // Get events by Manager ID
  //   const getEventsByManagerId = (managerId: string, params: TPaginationParams): Promise<TEvent> => {
  //     return httpAPI.get<TEvent>(`${baseEventPath}${eventsByManagerIdEndpoint}/${managerId}`, { params });
  //   };

  // // Get events by Planner ID
  // const getEventsByPlannerId = (plannerId: string, params: TPaginationParams): Promise<TEvent[]> => {
  //   return httpAPI.get<TEvent[]>(`${baseEventPath}${eventsByPlannerIdEndpoint}/${plannerId}`, { params });
  // };

  // // Get events by Institute ID
  // const getEventsByInstituteId = (instituteId: string, params: TPaginationParams): Promise<TEvent[]> => {
  //   return httpAPI.get<TEvent[]>(`${baseEventPath}${eventsByInstituteIdEndpoint}/${instituteId}`, { params });
  // };

  // // Get event by ID
  // const getEventById = (eventId: string, params: TPaginationParams): Promise<TEvent> => {
  //   return httpAPI.get<TEvent>(`${baseEventPath}${eventByIdEndpoint}/${eventId}`, { params });
  // };

  // // Delete event by ID
  // const deleteEventById = (eventId: string): Promise<void> => {
  //   return httpAPI.delete(`${baseEventPath}/${eventId}`);
  // };

  /**
   * Fetch a 3-month window (previous, current, next) of events.
   * instituteId is extracted from the JWT token on the backend.
   * @param currentMonth YYYY-MM string
   * @param includeSpanning whether to include events that span across months
   */
  const getEventsMonthWindow = (
    currentMonth: string,
    includeSpanning: boolean = true
  ): Promise<TEventsMonthWindowFlattened> => {
    const requestObj = {
      params: {
        currentMonth,
        includeSpanning,
      },
    };

    return httpAPI.events
      .get<TEventsMonthWindowResponse>(`/month-window`, requestObj)
      .then((response) => {
        if (!response || !response.data) {
          return {
            data: [],
            total: 0,
            page: 1,
            limit: 10,
            totalPages: 0,
            window: {
              currentMonth,
              data: [],
              navigation: {
                previousMonth: currentMonth,
                nextMonth: currentMonth,
              },
            },
          };
        }

        const body = response?.data ?? response;
        const monthResponse: TEventsMonthWindowResponse = Array.isArray(body)
          ? { currentMonth, data: body, count: body.length, navigation: { previousMonth: currentMonth, nextMonth: currentMonth } }
          : body;
        return normalizeMonthWindowEvents(monthResponse, currentMonth);
      })
      .catch((error) => {
        console.error('Month window events API Error:', error);
        const fallbackMonth = dayjs(currentMonth).format('YYYY-MM');
        const fallbackEvents = mockEventsList.slice(0, 5).map((e) => ({
          id: e.id,
          manager: e.manager,
          description: e.description,
          startDate: e.startDate,
          endDate: e.endDate,
          eventGroup: e.eventGroup,
          eventScope: e.eventScope,
          expenditureActual: e.expenditureActual,
          expenditureEstimate: e.expenditureEstimate,
          institute: e.institute,
          plannedBy: e.plannedBy,
          participants: [],
          staffAssignments: [],
          isMandatory: false,
          priority: 'medium' as const,
          purpose: e.purpose,
          remarks: e.remarks,
          title: e.title,
          status: e.status === 'scheduled' ? 'scheduled' : 'completed',
          venue: e.venue,
          relatedClass: undefined,
        })) as any[];

        return {
          data: fallbackEvents as any,
          total: fallbackEvents.length,
          page: 1,
          limit: 10,
          totalPages: 1,
          window: {
            currentMonth: fallbackMonth,
            data: [],
            navigation: {
              previousMonth: fallbackMonth,
              nextMonth: fallbackMonth,
            },
          },
        };
      });
  };

  return {
    getEventsList,
    getEventsMonthWindow,
    // createEvent,
    // getEventsByManagerId,
    // getEventsByPlannerId,
    // getEventsByInstituteId,
    // getEventById,
    // deleteEventById,
  };
};

/**
 * Pure helper to normalize month-window raw response into flattened events list.
 */
export function normalizeMonthWindowEvents(
  response: TEventsMonthWindowResponse,
  fallbackCurrentMonth?: string
): TEventsMonthWindowFlattened {
  const currentMonth =
    response.currentMonth || fallbackCurrentMonth || dayjs().format('YYYY-MM');
  const rawEvents: TMonthWindowRawEvent[] = response.data.flatMap(
    (m) => m.events || []
  );
  const uniqueMap = new Map<string, TMonthWindowRawEvent>();
  rawEvents.forEach((e) => uniqueMap.set(e._id, e));
  const normalized = Array.from(uniqueMap.values()).map((e) => {
    const scopesRaw = Array.isArray(e.eventScope)
      ? (e.eventScope as any[]).flat(Infinity).filter(Boolean)
      : [e.eventScope];
    const scopes = scopesRaw.filter(
      (s): s is string => typeof s === 'string'
    ) as any[];
    const primaryScope = (scopes[0] || 'institute') as any;
    return {
      id: e._id,
      manager: e.manager,
      description: e.description,
      startDate: new Date(e.startDate),
      endDate: new Date(e.endDate),
      eventGroup: e.eventGroup,
      eventScope: primaryScope,
      eventScopes: scopes as any,
      expenditureActual: e.expenditureActual,
      expenditureEstimate: e.expenditureEstimate,
      institute: e.institute,
      plannedBy: e.plannedBy,
      participants: [],
      staffAssignments: [],
      isMandatory: false,
      priority: 'medium' as const,
      purpose: e.purpose,
      remarks: e.remarks,
      title: e.description?.slice(0, 60) || 'Untitled Event',
      status: 'scheduled',
      venue: '',
      relatedClass: undefined,
    } as any;
  });
  return {
    data: normalized,
    total: normalized.length,
    page: 1,
    limit: normalized.length || 10,
    totalPages: 1,
    window: {
      currentMonth,
      data: response.data,
      navigation: response.navigation,
    },
  };
}
